import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { type AxiosError } from 'axios';

import { Button } from 'ui-components/components/button';
import { CurrencyInput } from 'ui-components/components/currency-input';
import { FlexColumn, Gap } from 'ui-components/components/flex';
import { LabelWithRequiredIndicator } from 'ui-components/components/label-with-required-indicator';
import { Input, version2 } from 'ui-components/components/input';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { Text, TextType } from 'ui-components/components/text';
import { GuidService } from 'ui-components/services/guid-service';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { NOTIFICATION_TYPE_ERROR, NotificationService } from 'ui-components/services/notification-service';
import { SearchableDropdownField } from 'ui-components/components/dropdown';

import { ModalPortal } from '../../../../../../components/modal-portal';
import Global from '../../../../../../store/global-selectors';
import { useSuppliers } from '../../../../../../pages/products-page/purchase-orders/hooks/use-suppliers';
import { createSupplierProduct } from '../../../../../../pages/products-page/suppliers/api/supplier-products-api';
import { useProductDetailsContext } from '../../../../use-product-details-context';
import { messages } from './add-supplier-modal.messages';
import styles from './add-supplier-modal.module.less';

type AddSupplierModalProps = {
  close: () => void;
  onSuccess: () => void;
};

export const AddSupplierModal = ({ close, onSuccess }: AddSupplierModalProps) => {
  const intl = useIntl();
  const modalHeaderId = useMemo(() => GuidService.newGuid(), []);
  const currencyCode = useSelector(Global.selectSellerDefaultCurrency);
  const { productId } = useProductDetailsContext();

  const { suppliers, isLoading: isSuppliersLoading } = useSuppliers();

  const [isLoading, setIsLoading] = useState(false);
  const [selectedSupplierId, setSelectedSupplierId] = useState<string | null>(null);
  const [supplierSku, setSupplierSku] = useState('');
  const [cost, setCost] = useState<number>(0);
  const [supplierError, setSupplierError] = useState('');

  const onSupplierSelected = useCallback((value: string) => {
    setSelectedSupplierId(value);
    setSupplierError('');
  }, []);

  const onSupplierSkuChanged = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSupplierSku(event.target.value);
  }, []);

  const onCostChanged = useCallback((value: string | number | null | undefined) => {
    const costNumber = parseFloat(String(value));
    setCost(isNaN(costNumber) ? 0 : costNumber);
  }, []);

  const handleSave = useCallback(async () => {
    if (!selectedSupplierId) {
      setSupplierError(intl.formatMessage(messages.supplierRequiredError));
      return;
    }

    if (!productId) {
      return;
    }

    setIsLoading(true);
    try {
      await createSupplierProduct({ productId, supplierId: Number(selectedSupplierId), supplierSku, cost });
      onSuccess();
      close();
    } catch (error) {
      const axiosError = error as AxiosError;
      const isDuplicateError = axiosError?.response?.status === 409;
      NotificationService.showNotification({
        title: <FormattedMessage {...messages.errorNotificationTitle} />,
        content: isDuplicateError ? (
          <FormattedMessage {...messages.duplicateSupplierError} />
        ) : (
          <FormattedMessage {...messages.errorNotificationContent} />
        ),
        notificationType: NOTIFICATION_TYPE_ERROR,
      });
    } finally {
      setIsLoading(false);
    }
  }, [selectedSupplierId, productId, supplierSku, cost, onSuccess, close, intl]);

  const isBusy = isLoading || isSuppliersLoading;

  return (
    <ModalPortal onClose={close}>
      <Modal modalHeaderId={modalHeaderId} size='medium'>
        <ModalHeader closeModal={close} id={modalHeaderId}>
          <Text textType={TextType.H3} value={messages.title} />
        </ModalHeader>
        <ModalBody>
          {isSuppliersLoading ? (
            <FlexColumn className={styles.loadingContainer}>
              <LoadingIndicator className={styles.loading} />
            </FlexColumn>
          ) : (
            <FlexColumn className={styles.content} gap={Gap.Space16}>
              <FlexColumn gap={Gap.Space8}>
                <LabelWithRequiredIndicator>
                  <Text textType={TextType.Label} value={messages.supplierLabel} />
                </LabelWithRequiredIndicator>
                <SearchableDropdownField
                  id='assign-supplier-select'
                  isDisabled={isBusy}
                  isRequiredError={!!supplierError}
                  onItemSelected={onSupplierSelected}
                  options={suppliers}
                  selectedItemValue={selectedSupplierId ?? undefined}
                />
              </FlexColumn>
              <FlexColumn gap={Gap.Space8}>
                <Text textType={TextType.Label} value={messages.supplierSkuLabel} />
                <Input
                  disabled={isBusy}
                  onChange={onSupplierSkuChanged}
                  placeholder={intl.formatMessage(messages.supplierSkuPlaceholder)}
                  value={supplierSku}
                  version={version2}
                />
              </FlexColumn>
              <FlexColumn gap={Gap.Space8}>
                <Text textType={TextType.Label} value={messages.unitCostLabel} />
                <CurrencyInput
                  className={styles.currencyInput}
                  currencyCode={currencyCode}
                  disabled={isBusy}
                  disallowNegative
                  onChange={onCostChanged}
                  value={cost}
                  showPlaceHolderForZero
                />
              </FlexColumn>
            </FlexColumn>
          )}
        </ModalBody>
        <ModalFooter>
          <Button disabled={isBusy} onClick={close} theme={THEME_CONSTANTS.tertiary}>
            <FormattedMessage {...messages.cancel} />
          </Button>
          <Button disabled={isBusy} onClick={handleSave} theme={THEME_CONSTANTS.primary}>
            <FormattedMessage {...messages.save} />
          </Button>
        </ModalFooter>
      </Modal>
    </ModalPortal>
  );
};
